import type { SortValue } from '@components';
import type { AppDispatchFn } from '@hooks/reduxHooks.ts';
import { SEARCH_THROTTLE_MS } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/constants.tsx';
import { buildSortRequest } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/___shared___/utils.ts';
import type { CommonSearchRequest, Filter } from '@types';
import { debounce } from 'lodash';

type SortMap = Partial<Record<string, string>>;

export const stringListToFilterRecord = (list: string[], key: string): Record<string, string[]> | null => {
    if (list.length === 0) {
        return null;
    }
    return {
        [key]: list,
    };
};

export const filtersToRecord = (filters: Filter[]): Record<string, string[]> => {
    return filters.reduce<Record<string, string[]>>((acc, { filter, value }) => {
        acc[filter] ??= [];
        acc[filter].push(value);
        return acc;
    }, {});
};

export const getFiltersFromSearchRequest = (request: CommonSearchRequest): Filter[] => {
    if (request.filters === null) return [];
    return Object.entries(request.filters).flatMap(([filter, values]) =>
        values.map((value) => ({
            filter,
            value,
        })),
    );
};
export const getSearchRequestWithNewFilter = (
    request: CommonSearchRequest,
    key: string,
    value: string,
): CommonSearchRequest => {
    const filter = request.filters ?? {};
    const prev = filter[key] ?? [];

    return { ...request, filters: { ...filter, [key]: [...prev, value] } };
};
export const getSearchRequestWithNewFilterBool = (
    request: CommonSearchRequest,
    key: string,
    value: boolean,
): CommonSearchRequest => {
    const strValue = value === true ? 'true' : 'false';
    return getSearchRequestWithNewFilter(request, key, strValue);
};

export const createDebouncedTextSearch = (
    dispatch: AppDispatchFn,
    actionCreator: (args: any) => any,
    sortMap: SortMap,
    wait: number = SEARCH_THROTTLE_MS,
    onSuccess?: (res: any) => void,
) =>
    debounce(async (value: string, sortState?: SortValue | null, filters?: Record<string, string[]>) => {
        const { sortBy, sortOrder } = buildSortRequest(sortState ?? null, sortMap);

        const mappedFilters = filters
            ? Object.entries(filters).reduce<Record<string, string[]>>((acc, [uiKey, vals]) => {
                  const clean = (vals ?? []).map(String).filter(Boolean);
                  if (!clean.length) return acc;

                  const backendKey = sortMap[String(uiKey)] ?? String(uiKey);
                  acc[backendKey] = clean;
                  return acc;
              }, {})
            : null;

        const res = await dispatch(
            actionCreator({
                payload: {
                    pageNumber: null,
                    pageSize: null,
                    textSearch: value,
                    sortBy,
                    sortOrder,
                    filters: mappedFilters && Object.keys(mappedFilters).length ? mappedFilters : null,
                },
            }),
        );
        onSuccess?.(res);
    }, wait);
